/*
  component: layout-grid-surface
  title: Layout Grid Surface
  props:
    - edit-label
  methods:
    - setLayout, getLayout, setComponentCatalog, setSectionRenderer
    - enterEditMode, exitEditMode, isEditMode
  events:
    - layout-surface-save, layout-surface-cancel, layout-change, layout-widget-change
  deps: layout-split-view, layout-split-editor, layout-split utils
*/
import {
  defaultSplitLayout,
  migrateToV2,
  collectSections,
  validateLayoutClient,
  MIN_SECTIONS
} from '../utils/layout-split.js';

class LayoutGridSurface extends HTMLElement {
  constructor() {
    super();
    this._layout = defaultSplitLayout();
    this._layoutSnapshot = null;
    this._componentCatalog = [];
    this._sectionRenderer = null;
    this._editMode = false;
    this._pendingInstanceConfigs = null;
  }

  connectedCallback() {
    this.render();
    this._attachToolbarListeners();
  }

  isEditMode() {
    return this._editMode;
  }

  setLayout(doc) {
    this._layout = migrateToV2(doc);
    this._renderGrid();
  }

  getLayout() {
    if (this._editMode) {
      const editor = this.querySelector('layout-split-editor');
      return editor ? editor.getLayout() : structuredClone(this._layout);
    }
    return structuredClone(this._layout);
  }

  setComponentCatalog(entries) {
    this._componentCatalog = Array.isArray(entries) ? entries : [];
    const editor = this.querySelector('layout-split-editor');
    if (editor) {
      editor.setComponentCatalog(this._componentCatalog);
    }
  }

  setSectionRenderer(fn) {
    this._sectionRenderer = typeof fn === 'function' ? fn : null;
    this._mountSectionContent();
  }

  setPendingInstanceConfigs(configMap) {
    this._pendingInstanceConfigs = configMap instanceof Map ? configMap : null;
  }

  enterEditMode() {
    if (this._editMode) return;
    this._layoutSnapshot = structuredClone(this.getLayout());
    this._editMode = true;
    this.classList.add('is-editing');
    this.render();
    this._attachToolbarListeners();
    this._mountSectionContent();
  }

  exitEditMode(restore = false) {
    if (!this._editMode) return;
    if (restore && this._layoutSnapshot) {
      this._layout = structuredClone(this._layoutSnapshot);
    }
    this._editMode = false;
    this._layoutSnapshot = null;
    this.classList.remove('is-editing');
    this.render();
    this._mountSectionContent();
    if (restore) {
      this.dispatchEvent(new CustomEvent('layout-surface-cancel', { bubbles: true }));
    }
  }

  render() {
    const editLabel = this.getAttribute('edit-label') || 'Editing layout';
    const toolbarHidden = this._editMode ? '' : 'hidden';

    this.innerHTML = `
      <div class="layout-grid-surface-inner">
        <div class="layout-grid-surface-toolbar card" ${toolbarHidden}>
          <span class="layout-grid-surface-toolbar-label">${this._escapeHtml(editLabel)}</span>
          <div class="layout-grid-surface-toolbar-actions">
            <button type="button" class="btn btn-secondary" data-action="surface-cancel">Cancel</button>
            <button type="button" class="btn btn-primary" data-action="surface-save">Save</button>
          </div>
        </div>
        <div class="layout-grid-surface-grid"></div>
      </div>
    `;

    this._renderGrid();
  }

  _renderGrid() {
    const container = this.querySelector('.layout-grid-surface-grid');
    if (!container) return;

    if (this._editMode) {
      container.innerHTML = '<layout-split-editor inline></layout-split-editor>';
      const editor = container.querySelector('layout-split-editor');
      editor.setComponentCatalog(this._componentCatalog);
      if (this._pendingInstanceConfigs?.size) {
        editor.setInstanceConfigValues(this._pendingInstanceConfigs);
        this._pendingInstanceConfigs = null;
      }
      editor.setLayout(this._layout);
      editor.addEventListener('layout-change', (e) => {
        this._layout = e.detail.layout;
        this._mountSectionContent();
      });
      editor.addEventListener('layout-widget-change', (e) => {
        this._refreshSectionPreview(e.detail.sectionId);
      });
      editor.addEventListener('layout-section-added', () => {
        this._mountSectionContent();
      });
    } else {
      container.innerHTML = '<layout-split-view></layout-split-view>';
      const view = container.querySelector('layout-split-view');
      view.setLayout(this._layout);
    }
  }

  _mountSectionContent() {
    if (!this._sectionRenderer) return;

    const mode = this._editMode ? 'edit' : 'view';
    const layout = this.getLayout();
    const sections = collectSections(layout.root);

    sections.forEach((section) => {
      const html = this._sectionRenderer(section, { mode }) ?? '';
      if (this._editMode) {
        const editor = this.querySelector('layout-split-editor');
        if (editor) {
          editor.setSectionContent(section.id, html);
          const slot = editor.querySelector(`[data-section-id="${section.id}"] .layout-split-editor-preview`);
          slot?.classList.add('layout-read-only');
        }
      } else {
        const view = this.querySelector('layout-split-view');
        view?.setSectionContent(section.id, html);
      }
    });
  }

  _refreshSectionPreview(sectionId) {
    if (!this._sectionRenderer || !this._editMode) return;
    const layout = this.getLayout();
    const section = collectSections(layout.root).find((s) => s.id === sectionId);
    if (!section) return;
    const editor = this.querySelector('layout-split-editor');
    if (!editor) return;
    const html = this._sectionRenderer(section, { mode: 'edit' }) ?? '';
    editor.setSectionContent(sectionId, html);
    const slot = editor.querySelector(`[data-section-id="${sectionId}"] .layout-split-editor-preview`);
    slot?.classList.add('layout-read-only');
  }

  _attachToolbarListeners() {
    this.querySelector('[data-action="surface-cancel"]')?.addEventListener('click', () => {
      this.exitEditMode(true);
    });

    this.querySelector('[data-action="surface-save"]')?.addEventListener('click', () => {
      const layout = this.getLayout();
      const validation = validateLayoutClient(layout);
      const sections = collectSections(layout.root);
      if (!validation.valid || sections.length < MIN_SECTIONS) {
        const errEl = this.querySelector('.layout-split-editor-error');
        if (errEl) {
          errEl.textContent = validation.error || 'Layout is invalid.';
        }
        return;
      }
      this._layout = layout;
      const editor = this.querySelector('layout-split-editor');
      const instanceConfigs = editor?.getInstanceConfigs?.() || [];
      this.dispatchEvent(new CustomEvent('layout-surface-save', {
        bubbles: true,
        detail: { layout, instanceConfigs }
      }));
    });
  }

  _escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('layout-grid-surface', LayoutGridSurface);
